import {
  Attachment,
  Content,
  ContentAccessUser,
  ContentAudience,
  ContentWorkflow,
  LockType,
  PublicationType,
  PublisherReviewInfo,
  UserProfile,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';

import Publication from '../model/Publication';
import { autoGenerateHref } from '../api/ContentUtil';
import { displayByline } from '../api/UtilsApi';

/**
 * Point-in-time snapshot of a document stored in APGL (not live editor state)
 * that can be presented to the user for viewing or editing.
 *
 * There are three types, distinguished by the `kind` property:
 * - `draft` — in-progress work + workflow.
 * - `finalizing` — tech-writer pre-distribution copy + workflow.
 * - `publication` — approved/published version (no workflow).
 */
export type LoadedDocument = LoadedDraft | LoadedFinalizing | LoadedPublication;

/** Which of the three document snapshots is loaded. */
export type LoadedDocumentKind = LoadedDocument['kind'];

interface LoadedDocumentBase {
  readonly contentId: string;
  readonly content: Content;
  readonly contentAudience: ContentAudience;
  readonly author: UserProfile;
  readonly coAuthors: readonly ContentAccessUser[];
}

/** Author's in-progress draft, not yet published. */
export interface LoadedDraft extends LoadedDocumentBase {
  readonly kind: 'draft';
  readonly workflow: ContentWorkflow;
  /** When the draft was first created. */
  readonly createdAt: DateTime;
}

/** Pre-distribution copy being polished by the assigned tech writer before external publication. */
export interface LoadedFinalizing extends LoadedDocumentBase {
  readonly kind: 'finalizing';
  readonly workflow: ContentWorkflow;
  readonly publicationDate: DateTime;
  readonly publicationType: PublicationType;
  readonly publicationState: WorkflowState;
  readonly contentOwner: UserProfile;
  readonly attachments: readonly Attachment[];
}

/** Approved and published content, ready to be viewed (may or may not be on the external APG site). */
export interface LoadedPublication extends LoadedDocumentBase {
  readonly kind: 'publication';
  readonly workflowId?: number;
  readonly publicationDate: DateTime;
  readonly publicationType: PublicationType;
  readonly publicationState: WorkflowState;
  readonly contentOwner: UserProfile;
  readonly attachments: readonly Attachment[];
  readonly publisherReviewInfo?: PublisherReviewInfo;
}

/**
 * True when a workflow lock is held by a user other than `currentUserId`.
 * Untyped locks predate `LockType` and are treated as user locks.
 */
export function isLockedByOtherUser(workflow: ContentWorkflow, currentUserId: string): boolean {
  const lock = workflow.lock;
  return (
    !!workflow.isLocked &&
    !!lock?.acquiredBy &&
    lock.acquiredBy !== currentUserId &&
    lock.lockType !== LockType.SYSTEM
  );
}

/** True when the workflow is locked by an automated system process. */
export function isLockedBySystem(workflow: ContentWorkflow): boolean {
  return !!workflow.isLocked && workflow.lock?.lockType === LockType.SYSTEM;
}

/**
 * True when the document's workflow lock is held by another user.
 * Returns false for publications, system locks, missing locks, and locks held
 * by the current user.
 */
export function isLockedByOther(doc: LoadedDocument, currentUserId: string): boolean {
  if (doc.kind === 'publication') return false;
  return isLockedByOtherUser(doc.workflow, currentUserId);
}

/** True when the loaded document has been published at least once. */
export function isPublished(doc: LoadedDocument): boolean {
  if (doc.kind === 'publication' || doc.kind === 'finalizing') return true;
  return !!doc.workflow.publicationDate;
}

/** Workflow version label (e.g. `"3.0"`); `undefined` for publications, which carry no draft workflow. */
export function getVersionLabel(doc: LoadedDocument): string | undefined {
  if (doc.kind !== 'draft' && doc.kind !== 'finalizing') return undefined;
  const workflowId = doc.workflow.draftWorkflowId;
  return workflowId !== undefined ? `${workflowId}.0` : undefined;
}

/** "Owner and co-authors" string for display. */
export function getAuthorsByline(doc: LoadedDocument): string {
  const workflow = doc.kind === 'draft' || doc.kind === 'finalizing' ? doc.workflow : undefined;
  return displayByline(doc.author, [...doc.coAuthors], workflow);
}

/** Last publication date for the document; `undefined` when never published. */
export function getLastPublicationDate(doc: LoadedDocument): DateTime | undefined {
  if (doc.kind === 'publication' || doc.kind === 'finalizing') return doc.publicationDate;
  return doc.workflow.publicationDate ?? undefined;
}

/** Audiences this document has already been published to; gates audience-tile selection. */
export function getPreviouslyPublishedAudiences(doc: LoadedDocument): readonly ContentAudience[] {
  if (doc.kind !== 'draft' && doc.kind !== 'finalizing') return [];
  return (doc.workflow.publicationWorkflows ?? []).map((w) => w.contentAudience);
}

/** Convert `mplService.getContent()` output to a {@link LoadedDraft}. */
export function draftToLoadedDocument(
  contentId: string,
  content: Content,
  createdAt: DateTime,
  workflow: ContentWorkflow,
  coAuthors: readonly ContentAccessUser[],
): LoadedDraft {
  autoGenerateHref(content.contentData, content.contentMetadata);
  return {
    kind: 'draft',
    contentId,
    content,
    contentAudience: workflow.contentAudience,
    // @ts-expect-error TS2322 - strict migration
    author: workflow.ownerProfile,
    coAuthors,
    workflow,
    createdAt,
  };
}

/** Convert `mplService.getPublication()` output to a {@link LoadedPublication}. */
export function publicationToLoadedDocument(
  contentId: string,
  publication: Publication,
  coAuthors: readonly ContentAccessUser[],
): LoadedPublication {
  autoGenerateHref(publication.content.contentData, publication.content.contentMetadata);
  return {
    kind: 'publication',
    contentId,
    workflowId: publication.workflowId,
    content: publication.content,
    contentAudience: publication.contentAudience,
    author: publication.contentOwner,
    coAuthors,
    publicationDate: publication.publicationDate,
    publicationType: publication.publicationType,
    publicationState: publication.publicationState,
    contentOwner: publication.contentOwner,
    // @ts-expect-error TS2322 - strict migration
    attachments: publication.attachments,
    publisherReviewInfo: publication.publisherReviewInfo,
  };
}

/** Convert a finalizing publication + its draft workflow to a {@link LoadedFinalizing}. */
export function finalizingToLoadedDocument(
  contentId: string,
  publication: Publication,
  workflow: ContentWorkflow,
  coAuthors: readonly ContentAccessUser[],
): LoadedFinalizing {
  autoGenerateHref(publication.content.contentData, publication.content.contentMetadata);
  return {
    kind: 'finalizing',
    contentId,
    content: publication.content,
    contentAudience: publication.contentAudience,
    author: publication.contentOwner,
    coAuthors,
    workflow,
    publicationDate: publication.publicationDate,
    publicationType: publication.publicationType,
    publicationState: publication.publicationState,
    contentOwner: publication.contentOwner,
    // @ts-expect-error TS2322 - strict migration
    attachments: publication.attachments,
  };
}
